import { CdkMenuModule, CdkMenuTrigger } from "@angular/cdk/menu";
import { OverlayModule } from "@angular/cdk/overlay";
import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    booleanAttribute,
    Component,
    computed,
    ElementRef,
    input,
    model,
    ModelSignal,
    viewChild,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Capacitor } from "@capacitor/core";
import dayjs from "dayjs";
import { toObservableSignal } from "ngxtension/to-observable-signal";
import { Icon } from "../../../icon/icon.component";
import { Label } from "../../../label/label.component";
import { BaseInput } from "../../base-input";
import { CalendarOutput, InputCalendar } from "../calendar/calendar.component";

@Component({
    selector: "app-date-picker",
    templateUrl: "./input-date-picker.component.html",
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        Icon,
        InputCalendar,
        CdkMenuModule,
        OverlayModule,
        Label,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: DatePicker,
            multi: true,
        },
    ],
})
export class DatePicker extends BaseInput<Date> {
    menuTrigger = viewChild(CdkMenuTrigger);

    inputValue = model<string | null>(null);
    inputValue$ = toObservable(this.inputValue);
    date = model<Date | null>(new Date());
    date$ = toObservable(this.date);
    icon = input("");
    platform = Capacitor.getPlatform();
    maxDate = input<Date | null>(null);
    minDate = input<Date | null>(null);

    yearRef = viewChild<ElementRef<HTMLInputElement>>("yearRef");
    monthRef = viewChild<ElementRef<HTMLInputElement>>("monthRef");
    dayRef = viewChild<ElementRef<HTMLInputElement>>("dayRef");

    year = model<string>("");
    year$ = toObservable(this.year);

    month = model<string>("");
    month$ = toObservable(this.month);

    day = model<string>("");
    day$ = toObservable(this.day);

    wrap = input<boolean, string>(false, { transform: booleanAttribute });

    _date = toObservableSignal(
        computed(() => {
            const year = this.year();
            const month = this.month();
            const day = this.day();

            // 모든 필드가 완전한 형식일 때만 Date 생성
            if (
                year?.length === 4 &&
                month?.length === 2 &&
                day?.length === 2
            ) {
                return new Date(Number(year), Number(month) - 1, Number(day));
            }

            return null;
        }),
    );

    valid = computed(() => {
        const date = this.value();
        if (!date) {
            return false;
        }

        const year = this.year();
        const month = this.month();
        const day = this.day();

        return year.length === 4 && month.length === 2 && day.length === 2;
    });

    readonly keyList = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    readonly controlKeys = [
        "Backspace",
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Delete",
    ];

    constructor() {
        super();

        this._date.subscribe({
            next: (date) => {
                if (date) {
                    if (
                        this.value() &&
                        dayjs(this.value()).isSame(dayjs(date), "day")
                    ) {
                        return;
                    }
                    this.writeValue(date);
                    this.date.set(date);
                }
            },
        });

        afterNextRender(() => {
            setTimeout(() => {
                if (this.value()) {
                    const date = dayjs(this.value()!);
                    this.year.set(date.format("YYYY"));
                    this.month.set(date.format("MM"));
                    this.day.set(date.format("DD"));
                }
            }, 100);
        });

        this.value$.subscribe({
            next: (value) => {
                if (value) {
                    const date = dayjs(value);
                    this.year.set(date.format("YYYY"));
                    this.month.set(date.format("MM"));
                    this.day.set(date.format("DD"));
                }
            },
        });
    }

    handleOpen(ev: Event) {
        if (this.disabled()) return;

        this.menuTrigger()?.open();
        ev.stopPropagation();
        ev.preventDefault();
    }

    ngAfterViewInit(): void {
        this.year$.subscribe({
            next: (year) => {
                if (year?.length === 4) {
                    this.monthRef()?.nativeElement.focus();
                }
            },
        });

        this.month$.subscribe({
            next: (month) => {
                if (month?.length === 2) {
                    this.dayRef()?.nativeElement.focus();
                }
            },
        });

        this.day$.subscribe({
            next: (day) => {
                if (day === "33") {
                    this.day.set("03");
                } else if (day === "00") {
                    this.day.set("01");
                } else if (Number(day) > 31) {
                    this.day.set(day.slice(1));
                }
            },
        });
    }

    handleBackspace(event: KeyboardEvent, modelSignal: ModelSignal<string>) {
        if (this.disabled()) return;

        if (event.key === "Backspace") {
            modelSignal.set("");
            event.preventDefault();
        }
    }

    handlePaste(event: ClipboardEvent): void {
        if (this.disabled()) return;

        const pasteData = event.clipboardData?.getData("text") || "";
        if (!/^\d+$/.test(pasteData)) {
            event.preventDefault();
        }
    }

    blockNonNumbers(event: KeyboardEvent): void {
        if (this.disabled()) return;

        if (!/^\d$/.test(event.key) && !this.controlKeys.includes(event.key)) {
            event.preventDefault();
        }
    }

    yearKeydown(event: KeyboardEvent): void {
        if (this.disabled()) return;

        if (this.keyList.includes(event.key) && this.year().length === 4) {
            this.year.set("");
        }

        this.blockNonNumbers(event);
        this.handleBackspace(event, this.year);

        if (this.keyList.includes(event.key) && this.year().length < 4) {
            this.year.set(this.year() + event.key);
            event.preventDefault();
        }
    }

    monthKeydown(event: KeyboardEvent): void {
        if (this.disabled()) return;

        if (this.keyList.includes(event.key) && this.month().length === 2) {
            this.month.set("");
        }

        this.blockNonNumbers(event);
        this.handleBackspace(event, this.month);

        if (this.keyList.includes(event.key)) {
            const value = this.month();
            let nextValue = value + event.key;

            if (Number(nextValue) > 12) {
                nextValue = "0" + event.key;
            }

            this.month.set(nextValue.slice(0, 2));
            event.preventDefault();
        }
    }

    dayKeydown(event: KeyboardEvent): void {
        if (this.disabled()) return;

        this.blockNonNumbers(event);
        this.handleBackspace(event, this.day);

        if (this.keyList.includes(event.key)) {
            let value = this.day();

            // 이미 2자리면 첫 번째 자리를 버리고 새 숫자 추가
            if (value.length === 2) {
                value = value.slice(1);
            }

            let nextValue = value + event.key;

            if (Number(nextValue) > 31) {
                nextValue = "0" + event.key;
            }

            if (nextValue === "00") {
                nextValue = "01";
            }

            this.day.set(nextValue.slice(0, 2));
            event.preventDefault();
        }
    }

    dayBlur(): void {
        if (this.disabled()) return;

        const day = this.day();
        if (day && day.length === 1) {
            this.day.set("0" + day);
        }
    }

    monthBlur(): void {
        if (this.disabled()) return;

        const month = this.month();
        if (month && month.length === 1) {
            this.month.set("0" + month);
        }
    }

    handleValueChange() {
        if (this.disabled()) return;

        const year = this.value()?.getFullYear().toString();
        const month = (this.value()?.getMonth()! + 1).toString();
        const day = this.value()?.getDate().toString();

        this.year.set(year!);
        this.month.set(month.length < 2 ? "0" + month : month);
        this.day.set(day && day.length < 2 ? "0" + day : day || "");
    }

    reset(ev: any) {
        ev.stopPropagation();
        ev.preventDefault();

        this.year.set("");
        this.month.set("");
        this.day.set("");
        this.yearRef()?.nativeElement.focus();
        this.value.set("" as any);
    }

    handleSelect(event: CalendarOutput) {
        if (this.disabled()) return;

        if (event.value) {
            this.date.set(event.value);
            this.year.set(event.value!.getFullYear().toString());
            const month = (event.value!.getMonth() + 1).toString();
            this.month.set(month.length < 2 ? "0" + month : month);
            const day = event.value!.getDate().toString();
            this.day.set(day.length < 2 ? "0" + day : day);
        }

        this.menuTrigger()?.close();
    }
}
